import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Download, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoFull } from "@/components/site/Logo";
import { API, api } from "@/lib/api";

export function HubGate({ error }) {
  if (error === "email_not_verified") {
    return (
      <HubMessage
        title="Verify your email first"
        body="Your purchase is linked to your account, but your email isn't verified yet. Verify it from your account page to unlock the hub."
        linkTo="/account"
        linkText="Go to my account"
        testId="hub-gate-verify"
      />
    );
  }
  return (
    <HubMessage
      title="This hub is for pack owners"
      body="The Sitebound Job Seeker Hub unlocks when you purchase the Complete Construction Job Seeker Pack — 94 resources across 10 sections, readable online and downloadable."
      linkTo="/product/complete-construction-job-seeker-pack"
      linkText="View the pack"
      testId="hub-gate-purchase"
    />
  );
}

export function HubMessage({ title, body, linkTo, linkText, testId }) {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 py-24 text-center">
      <Lock className="h-10 w-10 text-[#FF5500]" />
      <h1 className="font-display mt-6 text-4xl font-extrabold uppercase tracking-tight text-slate-100">
        {title}
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-slate-400">{body}</p>
      <Link
        data-testid={testId}
        to={linkTo}
        className="mt-8 bg-[#FF5500] px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
      >
        {linkText}
      </Link>
    </div>
  );
}

export default function Hub() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    api
      .get("/hub")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "error"));
  }, [user]);

  if (user === null) {
    return <div className="mx-auto max-w-7xl px-4 py-24 text-sm uppercase tracking-widest text-slate-500">Loading…</div>;
  }
  if (user === false) {
    return (
      <HubMessage
        title="Sign in to your hub"
        body="The Job Seeker Hub is where your pack lives online. Sign in with the email you purchased with — verify it once and everything links up."
        linkTo="/account"
        linkText="Sign in or create account"
        testId="hub-gate-signin"
      />
    );
  }
  if (error) return <HubGate error={error} />;
  if (!data) {
    return <div className="mx-auto max-w-7xl px-4 py-24 text-sm uppercase tracking-widest text-slate-500">Loading your hub…</div>;
  }

  const readSet = new Set(data.read || []);
  const totalRead = data.sections.reduce(
    (n, s) => n + s.resources.filter((r) => r.kind === "read" && readSet.has(r.id)).length, 0
  );
  const totalReadable = data.stats.read_online;

  return (
    <div data-testid="hub-page" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 md:py-16 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-6 border-b border-white/10 pb-8">
        <div className="flex items-center gap-5">
          <LogoFull imgClass="h-12" />
          <div>
            <div className="mono-label">Job Seeker Hub</div>
            <h1 className="font-display mt-1 text-3xl font-extrabold uppercase leading-none tracking-tight text-slate-100 sm:text-4xl">
              Complete Construction Job Seeker Pack
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="hub-stat-resources" className="font-mono-ind border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-300">
            {data.stats.resources} resources
          </span>
          <span className="font-mono-ind border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-300">
            {data.stats.sections} sections
          </span>
          <span data-testid="hub-stat-progress" className="font-mono-ind border border-[#FF5500]/40 bg-[#FF5500]/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#FF5500]">
            {totalRead}/{totalReadable} read
          </span>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.sections.map((section) => {
          const reads = section.resources.filter((r) => r.kind === "read");
          const downloads = section.resources.filter((r) => r.kind === "download");
          const readCount = reads.filter((r) => readSet.has(r.id)).length;
          const pct = reads.length ? Math.round((readCount / reads.length) * 100) : 0;
          return (
            <Link
              key={section.slug}
              data-testid={`hub-section-${section.slug}`}
              to={`/hub/${section.slug}`}
              className="group flex flex-col border border-white/10 bg-[#121418] p-6 transition-colors duration-300 hover:border-[#FF5500]/50"
            >
              <div className="font-display text-5xl font-extrabold text-[#FF5500]">{section.number}</div>
              <h2 className="font-display mt-4 text-2xl font-bold uppercase leading-tight tracking-tight text-slate-100">
                {section.name}
              </h2>
              <div className="mt-3 flex gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <BookOpen className="h-3.5 w-3.5 text-[#FF5500]" /> {reads.length} read online
                </span>
                <span>{downloads.length} downloads</span>
              </div>
              <div className="mt-auto pt-5">
                <div className="h-1 w-full bg-white/10">
                  <div className="h-1 bg-[#FF5500] transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="font-mono-ind mt-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {readCount}/{reads.length} read
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-12 border border-white/10 bg-[#0D0F12] p-6 text-center sm:p-8">
        <div className="mono-label">Backup copy</div>
        <p className="mx-auto mt-3 max-w-md text-xs leading-relaxed text-slate-400">
          Prefer files? Every template downloads individually inside its section — and you can grab
          the complete pack as a single ZIP anytime.
        </p>
        <a
          data-testid="hub-download-zip"
          href={`${API}/hub/download-zip`}
          className="mt-5 inline-flex items-center gap-2 border border-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white"
        >
          <Download className="h-3.5 w-3.5" /> Download full ZIP backup
        </a>
      </div>
    </div>
  );
}
